const React = require('react');
const { createElement: h, useEffect, useRef, useState } = React;

// ── reference highlighting ───────────────────────────────────────────────────
// Variable / node-output references like `@Key` or `@PIM Login.token` are shown
// as soft pills INSIDE the input, instead of plain text. Native <input>/<textarea>
// can't render styled spans, so we mirror the text in a backdrop div (with the
// references wrapped in pill spans) and make the real field's text transparent
// on top — the caret + selection stay native, the backdrop just paints color.
//
// Names can contain spaces (e.g. "PIM Login"), so we match against the KNOWN
// titles in variableNodes rather than a naive @\w+ regex. After a name we also
// swallow an optional `.deep.path` and a trailing `++` / `--` counter suffix.

function escapeHtmlRe(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Build one regex that matches `@<title>` for any known title, longest first
// (so "@PIM Login" wins over a hypothetical "@PIM"), plus optional path/suffix.
function buildRefRegex(variableNodes) {
  var titles = (variableNodes || [])
    .map(function(vn) { return String(vn && vn.title || '').trim(); })
    .filter(Boolean)
    .sort(function(a, b) { return b.length - a.length; });
  if (!titles.length) return null;
  var alt = titles.map(escapeHtmlRe).join('|');
  // @(name)(optional .path)(optional ++/--)
  return new RegExp('@(?:' + alt + ')(?:\\.[\\w.$\\-]+)?(?:\\+\\+|--)?', 'g');
}

// Resolve which `@token` the caret sits in, for a value that may hold several
// references. Returns { atIdx, end, query }:
//   atIdx — index of the `@` starting the token (or -1 if caret isn't in one)
//   end   — index just past the token: the end of a COMPLETE matched reference
//           if the caret falls within one, else the caret itself (mid-typing)
//   query — the text between `@` and `end` (the name[.path][++/--])
// `end` is what commit() replaces up to, so re-picking swaps only this ref.
function activeToken(value, caret, refRegex) {
  var pos = typeof caret === 'number' ? caret : value.length;
  // The token starts at the last `@` at or before the caret.
  var atIdx = value.lastIndexOf('@', Math.max(0, pos - 1));
  if (atIdx < 0) return { atIdx: -1, end: pos, query: '' };

  // If a COMPLETE reference starts exactly here and the caret is inside it,
  // the token spans the whole reference (so clicking a committed ref targets
  // all of it). Otherwise the token runs to the caret (still being typed).
  var end = pos;
  if (refRegex) {
    refRegex.lastIndex = 0;
    var m;
    while ((m = refRegex.exec(value)) !== null) {
      if (m.index === atIdx) {
        var mEnd = m.index + m[0].length;
        if (pos >= m.index && pos <= mEnd) end = mEnd;
        break;
      }
      if (m.index > atIdx) break;
      if (m.index === refRegex.lastIndex) refRegex.lastIndex++;
    }
  }
  return { atIdx: atIdx, end: end, query: value.slice(atIdx + 1, end) };
}

// Split `value` into an array of { text, isRef } segments for the backdrop.
function tokenizeRefs(value, refRegex) {
  if (!value) return [{ text: '', isRef: false }];
  if (!refRegex) return [{ text: value, isRef: false }];
  var segments = [];
  var lastIndex = 0;
  refRegex.lastIndex = 0;
  var m;
  while ((m = refRegex.exec(value)) !== null) {
    if (m.index > lastIndex) {
      segments.push({ text: value.slice(lastIndex, m.index), isRef: false });
    }
    segments.push({ text: m[0], isRef: true });
    lastIndex = m.index + m[0].length;
    if (m.index === refRegex.lastIndex) refRegex.lastIndex++; // guard zero-width
  }
  if (lastIndex < value.length) {
    segments.push({ text: value.slice(lastIndex), isRef: false });
  }
  return segments;
}

// ── helpers ──────────────────────────────────────────────────────────────────
function shortTypeLabel(dataType) {
  var raw = String(dataType || '').toLowerCase();
  if (raw === 'int' || raw === 'integer' || raw === 'number') return 'int';
  if (raw === 'date') return 'date';
  if (raw === 'bool' || raw === 'boolean') return 'bool';
  if (raw === 'json' || raw === 'object') return 'obj';
  if (raw === 'array') return 'arr';
  return 'str';
}

function isComplex(value) {
  return value !== null && value !== undefined && typeof value === 'object';
}

function summaryFor(value) {
  if (!isComplex(value)) return '';
  return Array.isArray(value)
    ? value.length + ' items'
    : Object.keys(value).length + ' fields';
}

// Pretty preview for a leaf value (truncated, dates formatted as-is).
function previewLeaf(value) {
  if (value === null) return 'null';
  if (value === undefined) return 'undef';
  var s = String(value);
  return s.length > 32 ? s.slice(0, 32) + '…' : s;
}

// Children to show when drilling into a value, as [{ key, label, value, suffix }].
//  • object            → one row per field   (suffix `fieldName`)
//  • array of objects  → first element's fields (suffix `0.fieldName`)
//  • array of scalars  → one row per element  (suffix `0`, `1`, …) ← strings/numbers
// Returns [] for scalars and empty arrays (nothing to drill into).
function childEntries(value) {
  if (Array.isArray(value)) {
    if (value.length === 0) return [];
    // Array of records → expose the shared field shape from element 0.
    if (isComplex(value[0])) {
      return Object.keys(value[0]).slice(0, 30).map(function(k) {
        return { key: '0.' + k, label: k, value: value[0][k], suffix: '0.' + k };
      });
    }
    // Array of scalars (e.g. table names) → expose the elements by index.
    return value.slice(0, 30).map(function(item, i) {
      return { key: String(i), label: String(i), value: item, suffix: String(i) };
    });
  }
  if (isComplex(value)) {
    return Object.keys(value).slice(0, 30).map(function(k) {
      return { key: k, label: k, value: value[k], suffix: k };
    });
  }
  return [];
}

// ── inline expandable field tree ─────────────────────────────────────────────
// Recursive row used to drill into an object/array value. Clicking a row
// commits the path; the chevron toggles expansion. See childEntries() for how
// arrays of records vs. arrays of scalars are expanded.
function FieldTreeRow(props) {
  var label = props.label;
  var value = props.value;
  var path = props.path;
  var depth = props.depth || 0;
  var onSelect = props.onSelect;
  var [open, setOpen] = useState(depth < 1);

  var isArr = Array.isArray(value);
  var isObj = !isArr && isComplex(value);
  var children = childEntries(value);
  var expandable = children.length > 0;

  var preview = isArr
    ? '[' + value.length + ']'
    : isObj
    ? '{' + Object.keys(value).length + '}'
    : previewLeaf(value);

  return h('div', { className: 'wf-suggest-tree-node' },
    h('div', {
      className: 'wf-suggest-tree-row',
      style: { paddingLeft: (8 + depth * 14) + 'px' },
      onMouseDown: function(e) { e.preventDefault(); onSelect(path); }
    },
      h('span', {
        className: 'wf-suggest-tree-caret' + (expandable ? '' : ' wf-suggest-tree-caret-empty'),
        onMouseDown: function(e) {
          if (!expandable) return;
          e.preventDefault();
          e.stopPropagation();
          setOpen(function(v) { return !v; });
        }
      }, expandable ? (open ? '▾' : '▸') : ''),
      h('span', { className: 'wf-suggest-tree-label' }, label),
      h('span', { className: 'wf-suggest-tree-preview' }, preview)
    ),
    expandable && open
      ? children.map(function(child) {
          var childPath = path ? path + '.' + child.suffix : child.suffix;
          return h(FieldTreeRow, {
            key: child.key, label: child.label, value: child.value, path: childPath,
            depth: depth + 1, onSelect: onSelect
          });
        })
      : null
  );
}

// Top-level wrapper that renders the "Full output" shortcut + the recursive
// tree for a single suggestion's runtime value.
function FieldTree(props) {
  var output = props.output;
  var onSelect = props.onSelect;     // (path|null) → commit
  var isArr = Array.isArray(output);
  var isObj = !isArr && isComplex(output);

  return h('div', { className: 'wf-suggest-tree' },
    h('div', {
      className: 'wf-suggest-tree-row wf-suggest-tree-row-full',
      onMouseDown: function(e) { e.preventDefault(); onSelect(null); }
    },
      h('span', { className: 'wf-suggest-tree-caret wf-suggest-tree-caret-empty' }, ''),
      h('span', { className: 'wf-suggest-tree-label' }, 'Full output'),
      h('span', { className: 'wf-suggest-tree-preview' },
        isArr ? '[' + output.length + ']' : isObj ? '{' + Object.keys(output).length + '}' : previewLeaf(output)
      )
    ),
    isArr && output.length === 0
      ? h('div', { className: 'wf-suggest-tree-empty' }, 'Empty array')
      : childEntries(output).map(function(child) {
          return h(FieldTreeRow, {
            key: child.key, label: child.label, value: child.value,
            path: child.suffix, depth: 0, onSelect: onSelect
          });
        })
  );
}

// ── suggestion row (collapsed view) ──────────────────────────────────────────
function SuggestionRow(props) {
  var vn = props.vn;
  var isOutput = !!props.isOutput;
  var active = !!props.active;
  var expanded = !!props.expanded;
  var onActivate = props.onActivate;   // click whole row
  var onToggle = props.onToggle;       // click chevron (complex only)

  var dataType = (vn && vn.paramValues && vn.paramValues.dataType) || '';
  var hasComplex = isComplex(vn._runtimeOutput);
  var summary = summaryFor(vn._runtimeOutput);
  var typeLabel = isOutput
    ? (hasComplex ? 'data' : 'value')
    : (hasComplex ? 'data' : shortTypeLabel(dataType));

  return h('div', {
    className: 'wf-suggest-row'
      + (active ? ' wf-suggest-row-active' : '')
      + (expanded ? ' wf-suggest-row-expanded' : ''),
    onMouseDown: function(e) {
      e.preventDefault();
      // Whole-row click: complex → expand inline; simple → commit.
      if (hasComplex) onToggle(); else onActivate();
    }
  },
    h('span', {
      className: 'wf-suggest-caret' + (hasComplex ? '' : ' wf-suggest-caret-empty')
    }, hasComplex ? (expanded ? '▾' : '▸') : ''),
    h('span', { className: 'wf-suggest-at' }, '@'),
    h('span', { className: 'wf-suggest-name' }, vn.title),
    summary ? h('span', { className: 'wf-suggest-meta' }, summary) : null,
    h('span', {
      className: 'wf-suggest-type' + ((isOutput || hasComplex) ? ' wf-suggest-type-out' : '')
    }, typeLabel)
  );
}

// ── main component ───────────────────────────────────────────────────────────
//
// Inline @-mention input + suggestion panel rendered DIRECTLY BELOW the input
// (no popups, no portals, no positioning math). Suggestion list shows two
// sections — Variables and From previous steps — and each complex entry can
// expand inline into a recursive field tree, so picking `Details.id` is a
// single click without leaving the sidebar.
//
// Props:
//   value, onChange, variableNodes, placeholder, disabled, className,
//   multiline, rows
//
//   variableNodes entries may carry:
//     - `_isNodeOutput: true`  → renders under "From previous steps"
//     - `_runtimeOutput: any`  → enables the inline field tree when complex
function VariableAutocompleteInput(props) {
  var value = String(props.value || '');
  var onChange = props.onChange || function() {};
  var variableNodes = props.variableNodes || [];
  var placeholder = props.placeholder || '';
  var disabled = !!props.disabled;
  var className = props.className || '';
  var multiline = !!props.multiline;
  var rows = Number.isFinite(props.rows) ? props.rows : 4;

  var [activeIdx, setActiveIdx] = useState(0);
  var [open, setOpen] = useState(false);
  var [expandedId, setExpandedId] = useState(null);
  // Caret position (selectionStart). Drives WHICH @token the dropdown targets
  // when a field holds several references — see activeToken() below.
  var [caret, setCaret] = useState(0);
  var inputRef = useRef(null);
  var panelRef = useRef(null);
  var backdropRef = useRef(null);

  // Reference pills painted behind the (transparent-text) input.
  var refRegex = buildRefRegex(variableNodes);
  var segments = tokenizeRefs(value, refRegex);

  // Sync our caret state from the DOM after any event that can move it.
  function trackCaret() {
    if (!inputRef.current) return;
    var pos = inputRef.current.selectionStart;
    if (typeof pos === 'number') setCaret(pos);
  }

  // Mirror the field's scroll position onto the backdrop so pills stay aligned
  // when the text overflows (long single-line value or scrolled textarea).
  function syncScroll() {
    if (!backdropRef.current || !inputRef.current) return;
    backdropRef.current.scrollTop = inputRef.current.scrollTop;
    backdropRef.current.scrollLeft = inputRef.current.scrollLeft;
  }
  useEffect(syncScroll, [value]);

  // Resolve the @token the caret is currently in. With MULTIPLE references in
  // one field, "the last @" is wrong — clicking inside the first ref must target
  // THAT ref, not whichever comes last. So the active token starts at the last
  // `@` at-or-before the caret, and ends at either the end of a complete matched
  // reference (so clicking re-targets the whole committed ref) or the caret (when
  // the user is mid-typing a new one).
  var token = activeToken(value, caret, refRegex);
  var atIdx = token.atIdx;
  var query = token.query.toLowerCase();

  // For filtering we match on the NAME portion only — strip a trailing
  // `.deep.path` and any `++`/`--` suffix. This is what lets clicking back into
  // a field that already holds a committed ref (e.g. `@List Uniconta…tables.24`)
  // re-open the dropdown on its source variable instead of finding no match.
  var queryBase = query.split('.')[0].replace(/(\+\+|--)\s*$/, '');

  var matches = atIdx >= 0
    ? variableNodes.filter(function(vn) {
        var name = String(vn && vn.title || '').trim();
        if (!name) return false;
        return queryBase === '' ? true : name.toLowerCase().indexOf(queryBase) >= 0;
      })
    : [];
  var varItems = matches.filter(function(vn) { return !vn._isNodeOutput; });
  var outItems = matches.filter(function(vn) { return !!vn._isNodeOutput; });
  var ordered = varItems.concat(outItems);

  var visible = open && atIdx >= 0 && ordered.length > 0 && !disabled;

  useEffect(function() {
    if (activeIdx >= ordered.length) setActiveIdx(0);
  }, [ordered.length]);

  // Collapse any expanded tree when the matching set changes (typing filters).
  useEffect(function() {
    if (!ordered.some(function(vn) { return vn.id === expandedId; })) {
      setExpandedId(null);
    }
  }, [matches.length]);

  // Click outside closes the panel.
  useEffect(function() {
    if (!visible) return;
    function onDown(e) {
      if (inputRef.current && inputRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
      setExpandedId(null);
    }
    document.addEventListener('mousedown', onDown);
    return function() { document.removeEventListener('mousedown', onDown); };
  }, [visible]);

  function commit(vn, selectedPath) {
    if (!vn) return;
    var name = String(vn.title || '').trim();
    if (!name) return;
    var suffix = selectedPath ? '.' + selectedPath : '';
    // Replace ONLY the active token [atIdx, token.end); keep any text that
    // follows (e.g. a second reference further along the field).
    var prefix = atIdx >= 0 ? value.slice(0, atIdx) : value;
    var rest = atIdx >= 0 ? value.slice(token.end) : '';
    var ref = '@' + name + suffix;
    onChange(prefix + ref + rest);
    setOpen(false);
    setActiveIdx(0);
    setExpandedId(null);
    // Put the caret right after the inserted reference so a follow-up `@`
    // starts a fresh token instead of re-entering this one.
    var nextCaret = prefix.length + ref.length;
    setCaret(nextCaret);
    if (inputRef.current) {
      inputRef.current.focus();
      try { inputRef.current.setSelectionRange(nextCaret, nextCaret); } catch (e) {}
    }
  }

  function activateRow(vn) {
    // Simple value → commit immediately. Complex → toggle inline tree.
    if (isComplex(vn._runtimeOutput)) {
      setExpandedId(function(prev) { return prev === vn.id ? null : vn.id; });
    } else {
      commit(vn);
    }
  }

  var fieldProps = {
    ref: inputRef,
    className: 'wf-params-input wf-params-input-highlight ' + (multiline ? 'wf-params-textarea ' : '') + className,
    value: value,
    placeholder: placeholder,
    disabled: disabled,
    onScroll: syncScroll,
    onChange: function(e) {
      onChange(e.target.value);
      setOpen(true);
      trackCaret();
    },
    onFocus: function() { setOpen(true); trackCaret(); },
    // Clicking / arrowing / selecting moves the caret → re-resolve which @token
    // the dropdown targets. Keep the panel open on click so re-clicking a
    // committed ref shows its dropdown.
    onClick: function() { setOpen(true); trackCaret(); },
    onSelect: trackCaret,
    onKeyUp: trackCaret,
    onKeyDown: function(e) {
      if (!visible) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx(function(p) { return Math.min(p + 1, ordered.length - 1); });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx(function(p) { return Math.max(p - 1, 0); });
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        activateRow(ordered[activeIdx]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (expandedId) setExpandedId(null);
        else setOpen(false);
      }
    }
  };
  if (multiline) fieldProps.rows = rows;
  else fieldProps.type = 'text';

  function renderSection(items, isOutputSection, headerKey, label, indexOffset) {
    if (!items.length) return [];
    var nodes = [];
    nodes.push(h('div', {
      key: headerKey,
      className: 'wf-suggest-section' + (indexOffset > 0 ? ' wf-suggest-section-border' : '')
    }, label));
    items.forEach(function(vn, i) {
      var idx = indexOffset + i;
      var isExpanded = expandedId === vn.id;
      nodes.push(h(SuggestionRow, {
        key: vn.id || vn.title,
        vn: vn,
        isOutput: isOutputSection,
        active: idx === activeIdx,
        expanded: isExpanded,
        onActivate: function() { commit(vn); },
        onToggle: function() {
          setActiveIdx(idx);
          setExpandedId(isExpanded ? null : vn.id);
        }
      }));
      if (isExpanded && isComplex(vn._runtimeOutput)) {
        nodes.push(h('div', { key: vn.id + '__tree', className: 'wf-suggest-tree-wrap' },
          h(FieldTree, {
            output: vn._runtimeOutput,
            onSelect: function(path) { commit(vn, path); }
          })
        ));
      }
    });
    return nodes;
  }

  // Backdrop that paints the reference pills behind the input. It mirrors the
  // exact text (so caret alignment is pixel-perfect) but only the @refs carry
  // a pill style; everything else is invisible plain text acting as a spacer.
  var backdrop = h('div', {
    ref: backdropRef,
    className: 'wf-params-input-backdrop' + (multiline ? ' wf-params-textarea' : ''),
    'aria-hidden': 'true'
  },
    segments.map(function(seg, i) {
      return seg.isRef
        ? h('mark', { key: i, className: 'wf-ref-pill' }, seg.text)
        : h('span', { key: i }, seg.text);
    }),
    // Trailing newline keeps the textarea backdrop height in sync when the
    // value ends with a newline (browsers collapse a final \n otherwise).
    multiline ? '\n' : null
  );

  return h('div', { className: 'wf-var-autocomplete' + (multiline ? ' wf-var-autocomplete-multiline' : '') },
    h('div', { className: 'wf-params-input-wrap' },
      backdrop,
      h(multiline ? 'textarea' : 'input', fieldProps)
    ),
    visible
      ? h('div', { ref: panelRef, className: 'wf-suggest-panel' },
          renderSection(varItems, false, '__hdr-var', 'Variables', 0)
            .concat(renderSection(outItems, true, '__hdr-out', 'From previous steps', varItems.length))
        )
      : null
  );
}

module.exports = VariableAutocompleteInput;
